// Diálogo de exportación de listado de Personal:
// - Selección de columnas (con grupos pre-armados)
// - Formato PDF / Excel
// - Orientación PDF (portrait / landscape)
// - Título personalizable
// Respeta los filtros activos del listado y exporta TODOS los registros que
// matchean (no solo la página visible).

import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid,
    Checkbox, FormControlLabel, FormControl, FormLabel, RadioGroup, Radio,
    TextField, Typography, Alert, CircularProgress, Stack, Divider,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import { personalColumnasExportablesJWT, personalExportarJWT } from '../../api/endpoints';

const PRIMARY = '#0277bd';
const BR = 4;

// Presets — agrupaciones rápidas para que el usuario no tenga que tildar uno a uno
const PRESETS = {
    basico:    { label: 'Básico (DNI, nombre, contacto, función, área)',
                 cols: ['dni', 'apellido', 'nombre', 'funcion', 'area', 'condicion', 'emailInst'] },
    completo:  { label: 'Completo (todos los datos)',
                 cols: null /* todas */ },
    contacto:  { label: 'Contacto (DNI, nombre, emails, teléfonos)',
                 cols: ['dni', 'apellido', 'nombre', 'emailInst', 'emailPers', 'telLab', 'celular'] },
    rrhh:      { label: 'RRHH (datos laborales)',
                 cols: ['dni', 'apellido', 'nombre', 'cuil', 'funcion', 'area', 'condicion', 'categoria',
                        'fechaIngreso', 'antiguedad', 'estado'] },
    personal:  { label: 'Datos personales (incluye nacimiento, domicilio)',
                 cols: ['dni', 'apellido', 'nombre', 'fechaNac', 'edad', 'sexo', 'estadoCivil',
                        'domicilio', 'localidad'] },
};

export default function ExportarPersonalDialog({ open, onClose, filtros, totalEstimado, onError, onSuccess }) {
    const [catalogo, setCatalogo] = useState([]);
    const [loadingCatalogo, setLoadingCatalogo] = useState(true);
    const [seleccionadas, setSeleccionadas] = useState(new Set(PRESETS.basico.cols));
    const [formato, setFormato] = useState('xlsx');
    const [orientation, setOrientation] = useState('landscape');
    const [titulo, setTitulo] = useState('Personal — Fiscalía de Estado');
    const [exportando, setExportando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        let cancel = false;
        setLoadingCatalogo(true);
        personalColumnasExportablesJWT()
            .then(r => { if (!cancel && r?.ok) setCatalogo(r.items || []); })
            .finally(() => { if (!cancel) setLoadingCatalogo(false); });
        return () => { cancel = true; };
    }, [open]);

    const toggle = (key) => {
        setSeleccionadas(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const aplicarPreset = (presetKey) => {
        const p = PRESETS[presetKey];
        if (!p) return;
        if (p.cols === null) {
            // Todas las columnas
            setSeleccionadas(new Set(catalogo.map(c => c.key)));
        } else {
            setSeleccionadas(new Set(p.cols));
        }
    };

    const exportar = async () => {
        if (seleccionadas.size === 0) {
            setError('Elegí al menos una columna.');
            return;
        }
        setExportando(true);
        setError('');
        try {
            // Mantener el orden del catálogo (= orden de SQL) para consistencia
            const columnas = catalogo.map(c => c.key).filter(k => seleccionadas.has(k));
            await personalExportarJWT({
                filtros,
                columnas,
                formato,
                orientation,
                titulo: titulo.trim() || 'Personal',
            });
            onSuccess && onSuccess();
            onClose && onClose();
        } catch (e) {
            const msg = e?.message || 'Error al exportar';
            setError(msg);
            onError && onError(msg);
        } finally {
            setExportando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ color: PRIMARY, fontWeight: 700 }}>
                Exportar listado de Personal
                {totalEstimado != null && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#666', fontWeight: 400 }}>
                        Se exportarán <strong>{totalEstimado}</strong> persona(s) según los filtros actuales.
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {loadingCatalogo ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        {/* Presets */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Plantillas rápidas
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {Object.entries(PRESETS).map(([k, p]) => (
                                    <Button key={k} size="small" variant="outlined" onClick={() => aplicarPreset(k)}
                                        sx={{ textTransform: 'none', borderRadius: BR }}>
                                        {p.label}
                                    </Button>
                                ))}
                                <Button size="small" variant="text" color="inherit" onClick={() => setSeleccionadas(new Set())}
                                    sx={{ textTransform: 'none' }}>
                                    Vaciar selección
                                </Button>
                            </Stack>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Columnas */}
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                            Columnas a incluir ({seleccionadas.size} seleccionada{seleccionadas.size !== 1 ? 's' : ''})
                        </Typography>
                        <Grid container spacing={0.5}>
                            {catalogo.map(c => (
                                <Grid key={c.key} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={seleccionadas.has(c.key)} onChange={() => toggle(c.key)} />}
                                        label={<Typography variant="body2">{c.header}</Typography>}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Formato + opciones */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl>
                                    <FormLabel sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700, color: '#555 !important' }}>
                                        Formato
                                    </FormLabel>
                                    <RadioGroup row value={formato} onChange={e => setFormato(e.target.value)}>
                                        <FormControlLabel value="xlsx" control={<Radio size="small" />}
                                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><GridOnIcon fontSize="small" sx={{ color: '#1d6f42' }} /> Excel</Box>} />
                                        <FormControlLabel value="pdf" control={<Radio size="small" />}
                                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PictureAsPdfIcon fontSize="small" sx={{ color: '#c62828' }} /> PDF</Box>} />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            {formato === 'pdf' && (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl>
                                        <FormLabel sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700, color: '#555 !important' }}>
                                            Orientación PDF
                                        </FormLabel>
                                        <RadioGroup row value={orientation} onChange={e => setOrientation(e.target.value)}>
                                            <FormControlLabel value="landscape" control={<Radio size="small" />} label="Apaisada (recomendado)" />
                                            <FormControlLabel value="portrait" control={<Radio size="small" />} label="Vertical" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid size={{ xs: 12 }}>
                                <TextField label="Título del documento" value={titulo}
                                    onChange={e => setTitulo(e.target.value)}
                                    size="small" fullWidth
                                    helperText="Aparece como encabezado en el archivo. Se usa también para el nombre del archivo."
                                />
                            </Grid>
                        </Grid>

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={exportando}>Cancelar</Button>
                <Button variant="contained" onClick={exportar}
                    disabled={exportando || loadingCatalogo || seleccionadas.size === 0}
                    sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#01579b' }, textTransform: 'none' }}>
                    {exportando ? <CircularProgress size={20} sx={{ color: 'white' }} /> :
                        formato === 'xlsx' ? 'Descargar Excel' : 'Descargar PDF'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
